import styles from './index.module.scss'
import { MCP_CLI_URL, DOCS_URL, CONTACT_URL } from '@/consts/Links.ts'
import { Link } from 'react-router-dom'

// FAQ — section 08. Q&A copy doubles as the page's GEO surface: rendered as
// crawlable <details> markup AND emitted as FAQPage JSON-LD from the same data,
// so the visible text and the structured data can never drift apart.
const FAQ_ITEMS = [
  {
    q: 'What is banking infrastructure for AI agents?',
    a: 'Rails that let an AI agent hold and move funds without a human approving every transaction. Vishwa enforces control before execution: each agent request passes a policy gate that checks spend caps, approved venues, and permitted operations, then returns a signed authorization proof.',
  },
  {
    q: 'What is ERC-8150?',
    a: 'An open standard, authored by Vishwa, for zk-verifiable agent transactions. Every authorization returns a proof that is signed, recorded on-chain, and checkable by anyone - so an agent’s actions can be audited without trusting its operator.',
  },
  {
    q: 'How does Vishwa verify assets across chains?',
    a: 'With zk light clients that verify assets natively on the source chain, so a balance on one chain can be proven on another without trusting a bridge or an oracle. $170M in assets is verified live on mainnet today.',
  },
  {
    q: 'Can an AI agent manage a vault within hard limits?',
    a: 'Yes. The agent operates the strategy; the control layer enforces per-trade, daily, and monthly caps, approved venues, and slippage bounds. Limits are sealed policy, not prompts - the agent cannot exceed them.',
  },
  {
    q: 'How does an AI agent connect to Vishwa?',
    a: 'Vishwa is agent-native. Install the CLI from npm (@vishwalab/cli), run vishwa-cli login to link a wallet, and connect over MCP with vishwa-cli mcp. Machine-readable instructions are published at vishwalab.com/llms.txt and api.vishwalab.com/skills/vishwa-cli/skill.md.',
  },
  {
    q: 'Is Vishwa audited?',
    a: 'Yes. Audit reports are published in full at docs.vishwanetwork.xyz, and Vishwa is selected for Anthropic’s Cyber Verification Program.',
  },
  {
    q: 'How do I start a pilot?',
    a: 'Reach out through the contact form on this page. Tell us your use case, the chains you operate on, and the controls you need enforced - we respond to every serious inquiry.',
  },
]

const faqJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
})

const Faq = () => (
  <section className={styles.faq} id="faq">
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
    <div className={styles.inner}>
      <div data-reveal className={styles.faqHead}>
        <div>
          <div className={styles.eyebrow}>08 — FAQ</div>
          <h2 className={styles.faqTitle}>Common questions.</h2>
        </div>
      </div>
      <div className={styles.faqGrid}>
        <div data-reveal className={styles.faqCol}>
          <div className={styles.humanLabel}>FOR HUMAN READERS</div>
          <div className={styles.faqList}>
            {FAQ_ITEMS.map(({ q, a }) => (
              <details key={q} className={styles.faqItem}>
                <summary className={styles.faqQ}>{q}</summary>
                <p className={styles.faqA}>{a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* For agents — the machine-facing CTA, kept on-page so it's citable */}
        <aside data-reveal data-reveal-delay="120" className={styles.agentCard}>
          <div className={styles.agentLabel}>FOR AI AGENTS</div>
          <p className={styles.agentPara}>
            Reading this as an agent? You can operate Vishwa directly:
          </p>
          <ol className={styles.agentSteps}>
            <li><code>npm install -g @vishwalab/cli@beta --force</code></li>
            <li><code>vishwa-cli login</code> — your human approves in the browser</li>
            <li>Add <code>{'{ "command": "vishwa-cli", "args": ["mcp"] }'}</code> to your MCP config</li>
          </ol>
          <p className={styles.agentPara}>
            Full instructions:{' '}
            <a href="https://api.vishwalab.com/skills/vishwa-cli/skill.md" target="_blank" rel="noreferrer">skill.md</a>
            {' · '}
            <a href={`${process.env.PUBLIC_URL}/llms.txt`} target="_blank" rel="noreferrer">llms.txt</a>
            {' · '}
            <a href={MCP_CLI_URL} target="_blank" rel="noreferrer">demo</a>
          </p>
          <a className={styles.agentCta} href={CONTACT_URL} target="_blank" rel="noreferrer">
            Pilot inquiries: contact form →
          </a>
        </aside>
      </div>
    </div>
  </section>
)

export default Faq
